import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { useSelector } from "react-redux"
import Notification from "@components/Notification/Notification"
import { selectMemberId } from "@/store/member/member.slice"
import { useIlbaEligibility } from "@/utils/hooks/useIlbaEligibility"
import { areBothIlbaProductsAffected } from "@/utils/helpers/ilbaRules"
import { replacePropsReturnXml } from "@/utils/helpers/formatters"
import { formatDayMonthYear } from "@/utils/helpers/dateHelpers"
import { useContentJsx } from "@/utils/hooks/useContent"

// Declare types
type IlbaProduct = {
  code: string
  name: string
  transferDate?: string
}

type IlbaTransferBannerProps = {
  affectedProducts: IlbaProduct[]
  unaffectedProducts: IlbaProduct[]
  actionDate: string
  onClose: () => void
}

type ExpiryItem = {
  code: string
  name: string
  expiryDate: string
}

type IlbaExpiryBannerProps = {
  expiringItems: ExpiryItem[]
  onClose: () => void
}

/**
 * Ilba Transfer Banner component
 *
 * @param affectedProducts - The products flagged for transfer to the ATO
 * @param unaffectedProducts - The products not flagged for transfer to the ATO
 * @param actionDate - Last day members can submit, shown as {action_date} in CMS body copy
 * @param onClose - The function to call when the transfer banner is closed
 * @returns The Ilba Transfer Banner component
 */
function IlbaTransferBanner({
  affectedProducts,
  unaffectedProducts,
  actionDate,
  onClose,
}: IlbaTransferBannerProps) {
  const isBothProductsAffected = areBothIlbaProductsAffected(affectedProducts)
  const contentKey = isBothProductsAffected
    ? "protect_your_super_ilba.both_gesb_super_and_west_state_super_accounts_affected"
    : "protect_your_super_ilba.single_affected_account"

  const {
    contentHeading: headingRaw,
    contentBody: alertContentRaw,
    contentFooter: footerRaw,
    valOne: linkText,
  } = useContentJsx({
    path: contentKey,
    noParse: true,
  })

  // Replaceable keywords
  const affectedNames = affectedProducts.map((p) => p.name).join(" and ")

  const affected_products_accounts = affectedNames

  const unaffected_product_account =
    unaffectedProducts.length > 0 ? unaffectedProducts[0].name : ""

  const productReplacements = {
    affected_products_accounts,
    affected_products_account: affected_products_accounts,
  }

  const headingContent = replacePropsReturnXml(
    headingRaw || "",
    productReplacements
  )

  // {action_date} = last inclusive election day from CSV action_date column (not blackout_start)
  const alertContent = replacePropsReturnXml(alertContentRaw || "", {
    ...productReplacements,
    action_date: formatDayMonthYear(actionDate),
  })

  const unaffectedProductContent =
    !isBothProductsAffected && unaffectedProducts.length > 0
      ? replacePropsReturnXml(footerRaw || "", {
          unaffected_product_account,
        })
      : null

  // Button CTA
  const alertButtonCTA = (
    <Link to="/personal-details#ilba-preference-module" className="button">
      {linkText ?? "Keep your account open"}
    </Link>
  )

  return (
    <Notification notificationType="alert" closeButton={true} onClose={onClose}>
      <h2>{headingContent}</h2>
      {alertContent}
      {unaffectedProductContent}
      <div className="text-right">{alertButtonCTA}</div>
    </Notification>
  )
}

/**
 * Ilba Expiry Banner component
 *
 * @param expiringItems - The products with preference expiry date expiring within 6 months
 * @param onClose - The function to call when the expiry banner is closed
 * @returns The Ilba Expiry Banner component
 */
function IlbaExpiryBanner({ expiringItems, onClose }: IlbaExpiryBannerProps) {
  // Raw content
  const {
    contentHeading: expiryHeading,
    contentBody: expiryContentBodyRaw,
    contentIntro: expiryContentIntroRaw,
    valOne: linkText,
  } = useContentJsx({
    path: "protect_your_super_ilba.gsu_wss_account_preference_will_expire",
    noParse: true,
  })

  const expiryContentRaw =
    expiringItems.length === 2 ? expiryContentBodyRaw : expiryContentIntroRaw

  // Replace the keywords in the content
  const expiryContent = replacePropsReturnXml(expiryContentRaw || "", {
    affected_product: expiringItems
      .map((product) => product.name)
      .join(" and "),
    affected_product_expiry_date: formatDayMonthYear(
      expiringItems[0]?.expiryDate ?? ""
    ),
    gsu_expiry_date: formatDayMonthYear(
      expiringItems.find((product) => product.code === "GSU")?.expiryDate ?? ""
    ),
    wss_expiry_date: formatDayMonthYear(
      expiringItems.find((product) => product.code === "WSS")?.expiryDate ?? ""
    ),
  })

  // Button CTA
  const expiryButtonCTA = (
    <Link to="/personal-details#ilba-preference-module" className="button">
      {linkText ?? "Renew my election"}
    </Link>
  )

  return (
    <Notification notificationType="alert" closeButton={true} onClose={onClose}>
      <h2>{expiryHeading}</h2>
      {expiryContent}
      <div className="text-right">{expiryButtonCTA}</div>
    </Notification>
  )
}

// Session storage keys
const TRANSFER_BANNER_DISMISSED_KEY = "IlbaTransferBannerDismissed"
const EXPIRY_BANNER_DISMISSED_KEY = "ilbaExpiryBannerDismissed"

/**
 * Ilba Homepage Banners component
 *
 * @returns The Ilba Homepage Banners component
 */
export default function IlbaHomepageBanners() {
  const memberId = useSelector(selectMemberId)

  // Hook
  const { isLoading, campaignState, IlbaTransferData, IlbaExpiryData } =
    useIlbaEligibility(String(memberId))

  // Alert and Expiry Banner Dismissed State / Session Storage
  const [isTransferDismissed, setIsTransferDismissed] = useState(false)
  const [isExpiryDismissed, setIsExpiryDismissed] = useState(false)

  useEffect(() => {
    if (sessionStorage.getItem(TRANSFER_BANNER_DISMISSED_KEY) === "true") {
      setIsTransferDismissed(true)
    }
    if (sessionStorage.getItem(EXPIRY_BANNER_DISMISSED_KEY) === "true") {
      setIsExpiryDismissed(true)
    }
  }, [TRANSFER_BANNER_DISMISSED_KEY, EXPIRY_BANNER_DISMISSED_KEY])

  const handleTransferDismiss = () => {
    sessionStorage.setItem(TRANSFER_BANNER_DISMISSED_KEY, "true")
    setIsTransferDismissed(true)
  }

  const handleExpiryDismiss = () => {
    sessionStorage.setItem(EXPIRY_BANNER_DISMISSED_KEY, "true")
    setIsExpiryDismissed(true)
  }

  const showTransferBanner =
    !isTransferDismissed && !isLoading && IlbaTransferData
  const showExpiryBanner = !isExpiryDismissed && !isLoading && IlbaExpiryData
  if (
    !campaignState.isWithinCampaign ||
    campaignState.isDuringBlackout ||
    (!showTransferBanner && !showExpiryBanner)
  ) {
    return null
  }

  // Return the banners
  return (
    <div className="ilba-homepage-banners">
      {!isTransferDismissed && !isLoading && IlbaTransferData && (
        <IlbaTransferBanner
          affectedProducts={IlbaTransferData.affectedProducts}
          unaffectedProducts={IlbaTransferData.unaffectedProducts}
          actionDate={campaignState.actionDate}
          onClose={handleTransferDismiss}
        />
      )}
      {!isExpiryDismissed && !isLoading && IlbaExpiryData && (
        <IlbaExpiryBanner
          expiringItems={IlbaExpiryData.expiringProducts}
          onClose={handleExpiryDismiss}
        />
      )}
    </div>
  )
}
